import { motion } from "framer-motion";
import { Send, Users } from "lucide-react";

/**
 * ChatScreen — inner UI for "The Green Room" phone mockup on the homepage.
 * Staggered chat bubbles reveal to give the feeling of a live convo.
 */
const MESSAGES = [
  { u: "iris", c: "#8b5cf6", side: "l", text: "yo just dropped a new EP 🔥", delay: 0.3 },
  { u: "you", c: "#FF4500", side: "r", text: "grabbing it now — the mix is fire", delay: 0.9 },
  { u: "kellan", c: "#22d3ee", side: "l", text: "matchering did SO much on track 3", delay: 1.4 },
  { u: "iris", c: "#8b5cf6", side: "l", text: "🎧 dropped: /track/anvil", delay: 1.9, isDrop: true },
];

export default function ChatScreen() {
  return (
    <div className="relative h-full w-full bg-[#0A0A0A] text-white flex flex-col">
      {/* Header */}
      <div className="border-b border-white/10 p-3 flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-sm">The Green Room</div>
          <div className="font-mono text-[9px] text-gray-500 flex items-center gap-1">
            <Users className="h-2.5 w-2.5" /> 47 online
          </div>
        </div>
        <div className="h-2 w-2 rounded-full bg-[#22c55e] animate-pulse" />
      </div>

      {/* Messages */}
      <div className="flex-1 px-3 py-3 space-y-2 overflow-hidden">
        {MESSAGES.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: m.side === "l" ? -20 : 20, y: 8 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: m.delay, duration: 0.5, ease: "easeOut" }}
            className={`flex ${m.side === "r" ? "justify-end" : "justify-start"} gap-2`}
          >
            {m.side === "l" && (
              <div
                className="h-6 w-6 rounded-full grid place-items-center font-mono text-[9px] font-bold text-black shrink-0"
                style={{ background: m.c }}
              >
                {m.u[0].toUpperCase()}
              </div>
            )}
            <div className="max-w-[80%]">
              {m.side === "l" && (
                <div className="font-mono text-[9px] mb-0.5" style={{ color: m.c }}>
                  @{m.u}
                </div>
              )}
              <div
                className={`font-mono text-[10px] px-2.5 py-1.5 rounded-2xl leading-snug ${
                  m.isDrop
                    ? "bg-[#FF4500]/15 border border-[#FF4500]/60 text-[#FF4500]"
                    : m.side === "r"
                    ? "bg-[#FF4500] text-black"
                    : "bg-white/10 text-white"
                }`}
              >
                {m.text}
              </div>
            </div>
          </motion.div>
        ))}
        {/* Typing */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.4, duration: 0.4 }}
          className="flex gap-1 pl-8 pt-1"
        >
          {[0, 0.2, 0.4].map((d, i) => (
            <motion.span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-gray-500"
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 0.8, repeat: Infinity, delay: d }}
            />
          ))}
        </motion.div>
      </div>

      {/* Composer */}
      <div className="border-t border-white/10 p-2 flex items-center gap-2">
        <div className="flex-1 bg-white/5 rounded-full px-3 py-1.5 font-mono text-[10px] text-gray-500">
          Say something…
        </div>
        <div className="h-7 w-7 rounded-full bg-[#FF4500] grid place-items-center">
          <Send className="h-3 w-3 text-black" />
        </div>
      </div>
    </div>
  );
}
